// Pure helpers for Web Push: can this browser receive it, and the base64url conversions the Push API needs.
// They take the navigator and window as arguments so unit tests can pass fakes.

export type PushSupportState =
  'unsupported' | 'ios-needs-install' | 'denied' | 'default' | 'granted'

export interface PushNavigatorLike {
  userAgent?: string
  platform?: string
  maxTouchPoints?: number
  /** iOS Safari sets this when the page runs from the Home Screen. */
  standalone?: boolean
  serviceWorker?: unknown
}

export interface PushWindowLike {
  PushManager?: unknown
  Notification?: { permission: NotificationPermission }
  matchMedia?: (query: string) => { matches: boolean }
}

/** iPhone, iPod, iPad, and iPadOS 13+ which reports itself as a touch-capable Mac. */
export function isIosDevice(nav: PushNavigatorLike): boolean {
  if (/iPad|iPhone|iPod/.test(nav.userAgent ?? '')) return true
  return nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1
}

/** Running from the Home Screen: `display-mode: standalone`, or the older iOS `navigator.standalone`. */
export function isStandalone(nav: PushNavigatorLike, win: PushWindowLike): boolean {
  if (nav.standalone === true) return true
  return win.matchMedia?.('(display-mode: standalone)').matches === true
}

export function pushSupportState(nav: PushNavigatorLike, win: PushWindowLike): PushSupportState {
  // Safari in a tab has no PushManager at all; the fix is installing the app, not another browser.
  if (isIosDevice(nav) && !isStandalone(nav, win)) return 'ios-needs-install'

  const supported = nav.serviceWorker !== undefined && !!win.PushManager && !!win.Notification
  if (!supported) return 'unsupported'

  return win.Notification!.permission
}

export function base64UrlToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** Unpadded base64url, the form the server stores for `p256dh` and `auth`. */
export function bufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  let binary = ''
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
